import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from './components/ui/avatar';
import { Camera, MapPin, Clock, User, Check, AlertCircle, Wifi, WifiOff, Shield, Calendar, LogOut, Upload, FileDown, RefreshCw, Eye, EyeOff, Moon, Sun, Image as ImageIcon, Briefcase } from 'lucide-react';

// --- START: Integrasi Firebase ---
import { initializeApp } from "firebase/app";
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  User as FirebaseUser
} from "firebase/auth";
import { 
  getFirestore, 
  doc, 
  setDoc, 
  collection, 
  onSnapshot,
  addDoc,
  query,
  getDocs,
  where
} from "firebase/firestore";

// Konfigurasi proyek Firebase Anda
const firebaseConfig = {
  apiKey: "AIzaSyAIuoJmPv_N4FNIUNpUKqmbhiyDHAbDq9I",
  authDomain: "sistem-absensi-sekolah-52be0.firebaseapp.com",
  projectId: "sistem-absensi-sekolah-52be0",
  storageBucket: "sistem-absensi-sekolah-52be0.appspot.com",
  messagingSenderId: "1019662796208",
  appId: "1:1019662796208:web:6cbba3bc0bffdbacd84275",
  measurementId: "G-62J825LZCQ"
};

// Inisialisasi Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
// --- END: Integrasi Firebase ---


// --- INTERFACES ---
interface Student {
  id: string;
  name: string;
  studentId: string;
  class: string;
  faceData: string;
  registeredAt: string;
  password: string;
  lastPhoto?: string;
}

interface Teacher {
    id: string;
    name: string;
    email: string;
    assignedClass: string;
}

interface AttendanceRecord {
  id: string;
  studentDocId: string;
  studentId: string;
  studentName: string;
  studentClass: string;
  timestamp: string;
  location: {
    latitude: number;
    longitude: number;
  };
  faceImage: string;
  status: 'present' | 'late' | 'absent';
  type: 'masuk' | 'pulang';
}

interface AppSettings {
    logoUrl?: string;
    backgroundUrl?: string;
}

interface LoggedInUser {
  uid: string;
  email: string | null;
  role: 'admin' | 'teacher';
  assignedClass?: string;
  name?: string;
}

// --- KOMPONEN UTAMA ---
const OnlineAttendanceSystem: React.FC = () => {
  // --- STATE & REFS ---
  const [currentView, setCurrentView] = useState<'login' | 'student' | 'admin' | 'teacher' | 'studentLogin' | 'adminLogin' | 'teacherLogin'>('login');
  const [students, setStudents] = useState<Student[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [currentStudent, setCurrentStudent] = useState<Student | null>(null);
  const [currentUser, setCurrentUser] = useState<LoggedInUser | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [loginError, setLoginError] = useState<string>('');
  const [selectedClass, setSelectedClass] = useState<string>('all');
  const [uploadMessage, setUploadMessage] = useState<string>('');
  const studentFileInputRef = useRef<HTMLInputElement>(null);
  const teacherFileInputRef = useRef<HTMLInputElement>(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [appSettings, setAppSettings] = useState<AppSettings>({});
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [attendanceTypeTemp, setAttendanceTypeTemp] = useState<'masuk' | 'pulang'>('masuk');
  const [cameraError, setCameraError] = useState<string>('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // --- KONSTANTA ---
  const SCHOOL_NAME = 'SMKS PGRI 2 JAMBI';
  const SCHOOL_PROGRAM = 'Teknik Sepeda Motor';
  
  const ATTENDANCE_LOCATION = {
    latitude: -1.6295605,
    longitude: 103.6520392,
    radius: 10,
    name: 'Area SMKS PGRI 2 JAMBI'
  };

  const ATTENDANCE_RULES = {
    masuk: { start: '06:00', end: '08:45', lateAfter: '07:40' },
    pulang: { start: '14:25', end: '16:30' },
  };

  // --- USEEFFECT HOOKS ---
  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') { root.classList.add('dark'); } 
    else { root.classList.remove('dark'); }
    localStorage.setItem('theme', theme);
  }, [theme]);
  
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user: FirebaseUser | null) => {
      if (user && user.email) {
        const teacherQuery = query(collection(db, "teachers"), where("email", "==", user.email));
        const teacherSnapshot = await getDocs(teacherQuery);

        if (!teacherSnapshot.empty) {
            const teacherData = teacherSnapshot.docs[0].data();
            setCurrentUser({ uid: user.uid, email: user.email, role: 'teacher', assignedClass: teacherData.assignedClass, name: teacherData.name });
            setCurrentView('teacher');
        } else {
            setCurrentUser({ uid: user.uid, email: user.email, role: 'admin' });
            setCurrentView('admin');
        }
      } else {
        setCurrentUser(null);
      }
      setIsLoading(false);
    });
    return () => unsubscribeAuth();
  }, []);
  
  useEffect(() => {
    const unsubSettings = onSnapshot(doc(db, 'settings', 'loginPage'), (doc) => {
      if (doc.exists()) setAppSettings(doc.data() as AppSettings);
    });

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    let unsubStudents = () => {};
    let unsubTeachers = () => {};
    let unsubAttendance = () => {};

    if (currentUser) {
        if (currentUser.role === 'admin') {
            unsubStudents = onSnapshot(collection(db, 'students'), (snapshot) => {
                setStudents(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Student[]);
            });
            unsubTeachers = onSnapshot(collection(db, 'teachers'), (snapshot) => {
                setTeachers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Teacher[]);
            });
            unsubAttendance = onSnapshot(collection(db, 'attendance'), (snapshot) => {
                setAttendanceRecords(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as AttendanceRecord[]);
            });
        } else if (currentUser.role === 'teacher' && currentUser.assignedClass) {
            const studentQuery = query(collection(db, 'students'), where("class", "==", currentUser.assignedClass));
            unsubStudents = onSnapshot(studentQuery, (snapshot) => {
                setStudents(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Student[]);
            });
            
            const attendanceQuery = query(collection(db, 'attendance'), where("studentClass", "==", currentUser.assignedClass));
            unsubAttendance = onSnapshot(attendanceQuery, (snapshot) => {
                setAttendanceRecords(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as AttendanceRecord[]);
            });
            
            setTeachers([]); 
        }
    } else {
        setStudents([]);
        setTeachers([]);
        setAttendanceRecords([]);
    }

    return () => {
      unsubSettings();
      unsubStudents();
      unsubTeachers();
      unsubAttendance();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [currentUser]);

  // --- FUNGSI AUTENTIKASI ---
  const handleAuthLogin = async (email: string, password: string) => {
    setIsLoading(true);
    setLoginError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      setLoginError('Email atau password salah.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthLogout = async () => {
    await signOut(auth);
    setLoginError('');
    setCurrentView('login');
  };

  const handleStudentLogin = async (studentId: string, password: string) => {
    setIsLoading(true);
    setLoginError('');
    const q = query(collection(db, "students"), where("studentId", "==", studentId), where("password", "==", password));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
        const studentDoc = querySnapshot.docs[0];
        setCurrentStudent({ id: studentDoc.id, ...studentDoc.data() } as Student);
        setCurrentView('student');
    } else {
        setLoginError('NIS atau password salah');
    }
    setIsLoading(false);
  };
  
  // --- FUNGSI KAMERA & ABSENSI ---
  const openCamera = async (type: 'masuk' | 'pulang') => {
    setAttendanceTypeTemp(type);
    setCameraError('');
    setCapturedPhoto(null);
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; }
        setIsCameraOpen(true);
      } catch (err) {
        setCameraError("Kamera tidak dapat diakses. Pastikan Anda memberikan izin.");
        setIsCameraOpen(true);
      }
    } else {
      setCameraError("Browser Anda tidak mendukung akses kamera.");
      setIsCameraOpen(true);
    }
  };

  const closeCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(track => track.stop()); }
    setIsCameraOpen(false);
    setCapturedPhoto(null);
    setCameraError('');
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      if (context) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg');
        setCapturedPhoto(dataUrl);
      }
    }
  };

  const handleRetake = () => { setCapturedPhoto(null); };
  
  const isTimeValid = (type: 'masuk' | 'pulang'): { valid: boolean; message: string, status: 'present' | 'late' } => {
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    const rules = ATTENDANCE_RULES[type];
    if (currentTime < rules.start || currentTime > rules.end) {
        return { valid: false, message: `Waktu absen ${type} tidak valid.`, status: 'present' };
    }
    if (type === 'masuk' && currentTime > ATTENDANCE_RULES.masuk.lateAfter) {
      return { valid: true, message: 'TERLAMBAT', status: 'late' };
    }
    return { valid: true, message: 'TEPAT WAKTU', status: 'present' };
  };

  const checkTimeWindow = (type: 'masuk' | 'pulang') => {
    const now = new Date();
    const currentTimeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    const rules = ATTENDANCE_RULES[type];
    if (currentTimeStr >= rules.start && currentTimeStr <= rules.end) {
        return { isOpen: true, message: '' };
    }
    if (currentTimeStr < rules.start) {
        return { isOpen: false, message: `Dibuka pukul ${rules.start} WIB` };
    }
    return { isOpen: false, message: `Telah ditutup pukul ${rules.end} WIB` };
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371e3;
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const checkLocation = (): Promise<{ valid: boolean; message: string; location?: { latitude: number; longitude: number } }> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ valid: false, message: 'Geolocation tidak didukung oleh browser ini.' });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const distance = calculateDistance(latitude, longitude, ATTENDANCE_LOCATION.latitude, ATTENDANCE_LOCATION.longitude);
          if (distance <= ATTENDANCE_LOCATION.radius) {
            resolve({ valid: true, message: 'Lokasi valid.', location: { latitude, longitude } });
          } else {
            resolve({ valid: false, message: `Anda berada ${Math.round(distance)}m dari lokasi. Maksimal ${ATTENDANCE_LOCATION.radius}m.` });
          }
        },
        () => {
          resolve({ valid: false, message: 'Gagal mendapatkan lokasi. Pastikan GPS aktif dan berikan izin.' });
        }
      );
    });
  };

  const handleConfirmAttendance = async () => {
    if (!currentStudent || !capturedPhoto) return;
    const timeCheck = isTimeValid(attendanceTypeTemp);
    if (!timeCheck.valid) { alert(timeCheck.message); closeCamera(); return; }
    const locationCheck = await checkLocation();
    if (!locationCheck.valid || !locationCheck.location) { alert(locationCheck.message); closeCamera(); return; }

    const newRecord = {
      studentDocId: currentStudent.id,
      studentId: currentStudent.studentId,
      studentName: currentStudent.name,
      studentClass: currentStudent.class,
      timestamp: new Date().toISOString(),
      location: locationCheck.location,
      faceImage: capturedPhoto,
      status: timeCheck.status,
      type: attendanceTypeTemp
    };
    
    await addDoc(collection(db, 'attendance'), newRecord);
    const studentRef = doc(db, 'students', currentStudent.id);
    await setDoc(studentRef, { lastPhoto: capturedPhoto }, { merge: true });

    closeCamera();
    alert(`Absen ${attendanceTypeTemp} berhasil! Status: ${timeCheck.message}`);
  };

  // --- FUNGSI ADMIN & KUSTOMISASI ---
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, field: 'logoUrl' | 'backgroundUrl') => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const result = e.target?.result as string;
      await setDoc(doc(db, 'settings', 'loginPage'), { [field]: result }, { merge: true });
    };
    reader.readAsDataURL(file);
  };
  
  const handleStudentFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadMessage('Memproses file...');
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim() !== '');
        let addedCount = 0;
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          if (values.length >= 4 && values[0]) {
            const newStudent = { studentId: values[0], name: values[1], class: values[2], password: values[3], faceData: `https://api.dicebear.com/7.x/avataaars/svg?seed=${values[1].replace(/\s/g, '')}`, registeredAt: new Date().toISOString() };
            await addDoc(collection(db, 'students'), newStudent);
            addedCount++;
          }
        }
        setUploadMessage(`Berhasil menambahkan ${addedCount} data murid baru.`);
      } catch (error) { setUploadMessage('Gagal memproses file.'); }
      setTimeout(() => setUploadMessage(''), 5000);
    };
    reader.readAsText(file);
  };

  const handleTeacherFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadMessage('Memproses file...');
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim() !== '');
        let addedCount = 0;
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          if (values.length >= 3 && values[0]) {
            const newTeacher = { email: values[0], name: values[1], assignedClass: values[2] };
            await addDoc(collection(db, 'teachers'), newTeacher);
            addedCount++;
          }
        }
        setUploadMessage(`Berhasil menambahkan ${addedCount} data wali kelas.`);
      } catch (error) { setUploadMessage('Gagal memproses file.'); }
      setTimeout(() => setUploadMessage(''), 5000);
    };
    reader.readAsText(file);
  };
  
  const downloadStudentTemplate = () => {
    const headers = ['NIS', 'Nama', 'Kelas', 'Password'];
    const sampleData = [['10001', 'Contoh Murid 1', 'XII TSM 1', 'password123']];
    const csvContent = [headers.join(','), ...sampleData.map(row => row.join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Template_Data_Murid.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const downloadTeacherTemplate = () => {
    const headers = ['Email', 'Nama', 'Kelas_Yang_Diampu'];
    const sampleData = [['wali@sekolah.com', 'Budi Santoso', 'XII TSM 1']];
    const csvContent = [headers.join(','), ...sampleData.map(row => row.join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Template_Data_Wali_Kelas.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const generateExcelReport = (period: 'daily' | 'monthly', forClass?: string) => {
    const headers = ['NIS', 'Nama', 'Kelas', 'Tanggal', 'Jam Masuk', 'Status Masuk', 'Jam Pulang'];
    let recordsToExport = attendanceRecords;
    if (forClass) {
        recordsToExport = attendanceRecords.filter(r => r.studentClass === forClass);
    }
    const dataToExport = recordsToExport.map(record => {
      const student = students.find(s => s.id === record.studentDocId);
      const recordDate = new Date(record.timestamp);
      return {
        NIS: student?.studentId || record.studentId,
        Nama: student?.name || record.studentName,
        Kelas: student?.class || record.studentClass,
        Tanggal: recordDate.toLocaleDateString('id-ID'),
        'Jam Masuk': record.type === 'masuk' ? recordDate.toLocaleTimeString('id-ID') : '-',
        'Status Masuk': record.type === 'masuk' ? (record.status === 'late' ? 'Terlambat' : 'Tepat Waktu') : '-',
        'Jam Pulang': record.type === 'pulang' ? recordDate.toLocaleTimeString('id-ID') : '-',
      };
    });

    const csvContent = [ headers.join(','), ...dataToExport.map(row => headers.map(header => `"${row[header as keyof typeof row]}"`).join(',')) ].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Laporan_Absensi_${forClass || 'Semua'}_${period}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getWeeklyAbsenceInfo = (studentDocId: string) => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // Minggu - 0, Senin - 1, ..., Sabtu - 6
    const startOfWeek = new Date(now);
    const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Sesuaikan jika hari ini Minggu
    startOfWeek.setDate(diff);
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    const studentRecordsThisWeek = attendanceRecords.filter(r =>
        r.studentDocId === studentDocId &&
        new Date(r.timestamp) >= startOfWeek &&
        new Date(r.timestamp) <= endOfWeek
    );

    const presentDays = new Set(
        studentRecordsThisWeek
            .filter(r => r.type === 'masuk')
            .map(r => new Date(r.timestamp).toDateString())
    ).size;
    
    let schoolDaysPassed = 0;
    if (dayOfWeek >= 1 && dayOfWeek <= 5) { // Senin sampai Jumat
        schoolDaysPassed = dayOfWeek;
    } else if (dayOfWeek === 6 || dayOfWeek === 0) { // Sabtu atau Minggu
        schoolDaysPassed = 5;
    }

    const absences = schoolDaysPassed - presentDays;
    
    if (absences >= 3) {
        return {
            showWarning: true,
            message: `PERINGATAN: Murid ini tidak hadir ${absences} kali dalam seminggu terakhir. Mohon untuk segera menghubungi orang tua/wali murid.`
        };
    }

    return { showWarning: false, message: '' };
  };
  
  // --- KOMPONEN KECIL ---
  const ThemeToggleButton = () => (
    <Button variant="ghost" size="icon" onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')} className="absolute top-4 right-4 text-slate-500 dark:text-slate-400">
      {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
    </Button>
  );

  // --- KOMPONEN VIEW ---
  const LoginView = () => (
    <div className="min-h-screen transition-all bg-cover bg-center" style={{ backgroundImage: appSettings.backgroundUrl ? `url(${appSettings.backgroundUrl})` : 'linear-gradient(to bottom right, #eff6ff, #e0e7ff)' }}>
      <div className="min-h-screen flex items-center justify-center p-4 bg-black/20 dark:bg-black/40">
        <Card className="w-full max-w-md dark:bg-slate-900/80 dark:backdrop-blur-sm dark:border-slate-700">
          <ThemeToggleButton />
          <CardHeader className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden border-2 border-white/50 shadow-lg"><img src={appSettings.logoUrl || "https://placehold.co/80x80/3b82f6/ffffff?text=LOGO"} alt="Logo" className="w-full h-full object-cover" /></div>
            <CardTitle className="dark:text-white">Sistem Absensi Online</CardTitle>
            <CardDescription className="dark:text-slate-400">{SCHOOL_NAME} - {SCHOOL_PROGRAM}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button className="w-full" size="lg" onClick={() => setCurrentView('studentLogin')}><User className="mr-2 h-5 w-5"/>Masuk Sebagai Murid</Button>
            <Button className="w-full" size="lg" onClick={() => setCurrentView('teacherLogin')} variant="outline"><Briefcase className="mr-2 h-5 w-5"/>Masuk Sebagai Wali Kelas</Button>
            <Button className="w-full" size="lg" onClick={() => setCurrentView('adminLogin')} variant="outline"><Shield className="mr-2 h-5 w-5"/>Masuk Sebagai Admin</Button>
          </CardContent>
          <div className="p-6 pt-0"><p className="text-xs text-muted-foreground dark:text-slate-500 text-center">Attendance App v1.0 © 2025 EL Su ENG</p></div>
        </Card>
      </div>
    </div>
  );

  const AuthLoginView = ({ role }: { role: 'admin' | 'teacher' }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => { return () => { setLoginError(''); } }, []);

    const title = role === 'admin' ? 'Login Admin' : 'Login Wali Kelas';

    return (
        <div className="min-h-screen transition-all bg-cover bg-center" style={{ backgroundImage: appSettings.backgroundUrl ? `url(${appSettings.backgroundUrl})` : 'linear-gradient(to bottom right, #eff6ff, #e0e7ff)' }}>
            <div className="min-h-screen flex items-center justify-center p-4 bg-black/20 dark:bg-black/40">
                <Card className="w-full max-w-md dark:bg-slate-900/80 dark:backdrop-blur-sm dark:border-slate-700 relative">
                    <ThemeToggleButton />
                    <Button variant="ghost" onClick={() => setCurrentView('login')} className="absolute top-4 left-4 text-sm z-10">← Kembali</Button>
                    <CardHeader className="text-center pt-16">
                        <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden border-2 border-white/50 shadow-lg"><img src={appSettings.logoUrl || "https://placehold.co/80x80/3b82f6/ffffff?text=LOGO"} alt="Logo" className="w-full h-full object-cover" /></div>
                        <CardTitle className="text-2xl font-bold dark:text-white">{title}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="authEmail">Email</Label>
                            <Input id="authEmail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@contoh.com" disabled={isLoading} autoComplete="off" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="authPassword">Password</Label>
                            <div className="relative">
                                <Input id="authPassword" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="********" disabled={isLoading} className="pr-10" autoComplete="new-password"/>
                                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600">{showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button>
                            </div>
                        </div>
                        {loginError && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" />{loginError}</div>}
                        <Button className="w-full" onClick={() => handleAuthLogin(email, password)} disabled={!email || !password || isLoading}>{isLoading ? 'Memproses...' : 'Masuk'}</Button>
                    </CardContent>
                    <div className="p-6 pt-0"><p className="text-xs text-muted-foreground dark:text-slate-500 text-center w-full font-serif">Attendance App v1.0 © 2025 EL Su ENG. All rights reserved</p></div>
                </Card>
            </div>
        </div>
    );
  };

  const StudentLoginView = () => {
    const [studentId, setStudentId] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => { return () => { setLoginError(''); } }, []);

    return (
      <div className="min-h-screen transition-all bg-cover bg-center" style={{ backgroundImage: appSettings.backgroundUrl ? `url(${appSettings.backgroundUrl})` : 'linear-gradient(to bottom right, #eff6ff, #e0e7ff)' }}>
        <div className="min-h-screen flex items-center justify-center p-4 bg-black/20">
          <Card className="w-full max-w-md relative dark:bg-slate-900/80 dark:backdrop-blur-sm dark:border-slate-700">
            <ThemeToggleButton />
            <Button variant="ghost" onClick={() => setCurrentView('login')} className="absolute top-4 left-4 text-sm z-10">← Kembali</Button>
            <CardHeader className="text-center pt-16">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden border-2 border-white/50 shadow-lg"><img src={appSettings.logoUrl || "https://placehold.co/80x80/3b82f6/ffffff?text=LOGO"} alt="Logo Murid" className="w-full h-full object-cover" /></div>
              <CardTitle className="text-2xl font-bold dark:text-white">Login Murid</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2"><Label htmlFor="studentId">NIS (Nomor Induk Murid)</Label><Input id="studentId" value={studentId} onChange={(e) => setStudentId(e.target.value)} placeholder="Masukkan NIS" autoComplete="off" /></div>
              <div className="space-y-2">
                <Label htmlFor="studentPassword">Password</Label>
                <div className="relative">
                  <Input id="studentPassword" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Masukkan password" className="pr-10" autoComplete="new-password"/>
                   <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600">{showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button>
                </div>
              </div>
              {loginError && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" />{loginError}</div>}
              <Button className="w-full" onClick={() => handleStudentLogin(studentId, password)} disabled={!studentId || !password || isLoading}>{isLoading ? 'Memproses...' : 'Masuk'}</Button>
            </CardContent>
            <div className="p-6 pt-0"><p className="text-xs text-muted-foreground dark:text-slate-500 text-center w-full font-serif">Attendance App v1.0 © 2025 EL Su ENG. All rights reserved</p></div>
          </Card>
        </div>
      </div>
    );
  };

  const StudentView = () => {
    const todayRecords = attendanceRecords.filter(record => new Date(record.timestamp).toDateString() === new Date().toDateString() && record.studentDocId === currentStudent?.id);
    const hasMasukToday = todayRecords.some(r => r.type === 'masuk');
    const hasPulangToday = todayRecords.some(r => r.type === 'pulang');
    const masukWindow = checkTimeWindow('masuk');
    const pulangWindow = checkTimeWindow('pulang');

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 p-4 transition-colors">
        <div className="max-w-2xl mx-auto space-y-4">
          <div className="relative flex justify-between items-center">
            <Button variant="ghost" onClick={() => { setCurrentStudent(null); setCurrentView('login'); }} className="text-muted-foreground dark:text-slate-400">
              ← Keluar
            </Button>
             <ThemeToggleButton />
            {isOnline ? <Wifi className="w-5 h-5 text-green-500" /> : <WifiOff className="w-5 h-5 text-red-500" />}
          </div>
          
          <div>
            <h1 className="text-2xl font-bold">Portal Absensi Murid</h1>
            <p className="text-muted-foreground dark:text-slate-400">Selamat datang di sistem absensi {SCHOOL_NAME}</p>
          </div>
          
          {currentStudent && (
            <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm flex items-center gap-4 border dark:border-slate-700">
              <Avatar className="w-12 h-12">
                <AvatarImage src={currentStudent.lastPhoto || currentStudent.faceData} />
                <AvatarFallback>{currentStudent.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <div className="font-semibold text-lg">{currentStudent.name}</div>
                <div className="text-sm text-muted-foreground dark:text-slate-400">NIS: {currentStudent.studentId} | Kelas: {currentStudent.class}</div>
              </div>
            </div>
          )}

          <Card className="border-blue-200 bg-blue-50/50 dark:bg-slate-800 dark:border-blue-900/50">
              <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-blue-800 dark:text-blue-300">
                      <AlertCircle className="w-5 h-5" />
                      Informasi Penting Absensi
                  </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-700 dark:text-slate-300 space-y-3">
                  <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 mt-1 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                      <div>
                          <h3 className="font-semibold">Waktu Absensi</h3>
                          <ul className="list-disc list-inside pl-1">
                              <li><b>Absen Masuk:</b> {ATTENDANCE_RULES.masuk.start} - {ATTENDANCE_RULES.masuk.end} WIB.</li>
                              <li>Dianggap <b>terlambat</b> setelah pukul <b>{ATTENDANCE_RULES.masuk.lateAfter} WIB</b>.</li>
                              <li><b>Absen Pulang:</b> {ATTENDANCE_RULES.pulang.start} - {ATTENDANCE_RULES.pulang.end} WIB.</li>
                          </ul>
                      </div>
                  </div>
                  <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 mt-1 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                      <div>
                          <h3 className="font-semibold">Lokasi Absensi</h3>
                          <p>Absensi hanya dapat dilakukan dalam radius <b>{ATTENDANCE_LOCATION.radius} meter</b> dari area sekolah.</p>
                      </div>
                  </div>
              </CardContent>
          </Card>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm flex justify-between items-center border dark:border-slate-700">
              <div>
                <p className="text-sm text-muted-foreground dark:text-slate-400">Absen Masuk</p>
                <p className={`text-lg font-semibold ${hasMasukToday ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
                  {hasMasukToday ? 'Sudah Absen' : 'Belum Absen'}
                </p>
              </div>
              <Clock className={`w-8 h-8 ${hasMasukToday ? 'text-green-500' : 'text-yellow-500'}`} />
            </div>
            <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm flex justify-between items-center border dark:border-slate-700">
              <div>
                <p className="text-sm text-muted-foreground dark:text-slate-400">Absen Pulang</p>
                 <p className={`text-lg font-semibold ${hasPulangToday ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
                  {hasPulangToday ? 'Sudah Absen' : 'Belum Absen'}
                </p>
              </div>
              <Clock className={`w-8 h-8 ${hasPulangToday ? 'text-green-500' : 'text-yellow-500'}`} />
            </div>
          </div>

          <div className="space-y-3 pt-2">
            <div>
              <Button size="lg" className="w-full" onClick={() => openCamera('masuk')} disabled={hasMasukToday || !masukWindow.isOpen}>
                <Camera className="w-5 h-5 mr-2" />
                {hasMasukToday ? 'Telah Absen Masuk' : 'Absen Masuk'}
              </Button>
              { !masukWindow.isOpen && !hasMasukToday && <p className="text-xs text-center text-red-500 mt-1">{masukWindow.message}</p> }
            </div>
            <div>
              <Button size="lg" className="w-full" variant="outline" onClick={() => openCamera('pulang')} disabled={!hasMasukToday || hasPulangToday || !pulangWindow.isOpen}>
                <Camera className="w-5 h-5 mr-2" />
                {hasPulangToday ? 'Telah Absen Pulang' : 'Absen Pulang'}
              </Button>
               { !pulangWindow.isOpen && hasMasukToday && !hasPulangToday && <p className="text-xs text-center text-red-500 mt-1">{pulangWindow.message}</p> }
            </div>
          </div>

          {todayRecords.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-lg">Riwayat Absen Hari Ini</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {todayRecords.map(record => (
                      <div key={record.id} className="flex items-center justify-between p-2 border dark:border-slate-700 rounded">
                        <div>
                          <span className="font-medium">{record.type === 'masuk' ? 'Absen Masuk' : 'Absen Pulang'}</span>
                          <p className="text-sm text-muted-foreground dark:text-slate-400">{new Date(record.timestamp).toLocaleTimeString('id-ID')} - {record.status === 'late' ? 'Terlambat' : 'Tepat Waktu'}</p>
                        </div>
                        <Avatar className="w-10 h-10"><AvatarImage src={record.faceImage} /></Avatar>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
        </div>
      </div>
    );
  };

  const AdminView = () => {
    const filteredStudents = selectedClass === 'all' ? students : students.filter(s => s.class === selectedClass);
    const logoInputRef = useRef<HTMLInputElement>(null);
    const bgInputRef = useRef<HTMLInputElement>(null);
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 p-4 transition-colors">
        <div className="max-w-7xl mx-auto">
          <Card>
            <CardHeader className="flex flex-row justify-between items-center">
              <div><CardTitle>Dashboard Admin</CardTitle><CardDescription>{SCHOOL_NAME}</CardDescription></div>
              <div className="flex items-center gap-2"><ThemeToggleButton /><Button variant="outline" onClick={handleAuthLogout}><LogOut className="w-4 h-4 mr-2" /> Keluar</Button></div>
            </CardHeader>
            <CardContent className="space-y-6">
              <Card><CardHeader><CardTitle>Pengaturan Tampilan</CardTitle></CardHeader><CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2"><Label>Logo Halaman Login</Label><div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden border"><img src={appSettings.logoUrl || "https://placehold.co/80x80/3b82f6/ffffff?text=LOGO"} alt="Logo Preview" className="w-full h-full object-cover" /></div>
                  <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'logoUrl')} />
                  <Button variant="outline" onClick={() => logoInputRef.current?.click()}><Upload className="w-4 h-4 mr-2" /> Ganti Logo</Button>
                </div></div>
                <div className="space-y-2"><Label>Background Halaman Login</Label><div className="flex items-center gap-4">
                  <div className="w-32 h-16"><img src={appSettings.backgroundUrl || "https://placehold.co/128x72/e0e7ff/333333?text=BG"} alt="Background Preview" className="w-full h-full rounded-md object-cover border" /></div>
                  <input ref={bgInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'backgroundUrl')} />
                  <Button variant="outline" onClick={() => bgInputRef.current?.click()}><ImageIcon className="w-4 h-4 mr-2" /> Ganti Background</Button>
                </div></div>
              </CardContent></Card>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card><CardHeader><CardTitle className="text-base">Manajemen Data Murid</CardTitle></CardHeader><CardContent className="flex flex-col gap-2">
                  <Button onClick={downloadStudentTemplate} variant="outline"><FileDown className="w-4 h-4 mr-2" /> Download Template</Button>
                  <input ref={studentFileInputRef} type="file" accept=".csv" onChange={handleStudentFileUpload} className="hidden" />
                  <Button onClick={() => studentFileInputRef.current?.click()}><Upload className="w-4 h-4 mr-2" /> Upload Data Murid</Button>
                  {uploadMessage && <p className="text-xs text-green-600 mt-2">{uploadMessage}</p>}
                </CardContent></Card>
                <Card><CardHeader><CardTitle className="text-base">Manajemen Wali Kelas</CardTitle></CardHeader><CardContent className="flex flex-col gap-2">
                  <Button onClick={downloadTeacherTemplate} variant="outline"><FileDown className="w-4 h-4 mr-2" /> Download Template</Button>
                  <input ref={teacherFileInputRef} type="file" accept=".csv" onChange={handleTeacherFileUpload} className="hidden" />
                  <Button onClick={() => teacherFileInputRef.current?.click()}><Upload className="w-4 h-4 mr-2" /> Upload Wali Kelas</Button>
                </CardContent></Card>
                <Card><CardHeader><CardTitle className="text-base">Export Laporan</CardTitle></CardHeader><CardContent className="flex flex-col gap-2">
                  <Button onClick={() => generateExcelReport('daily')} variant="outline"><Calendar className="w-4 h-4 mr-2" /> Laporan Harian</Button>
                  <Button onClick={() => generateExcelReport('monthly')} variant="outline"><Calendar className="w-4 h-4 mr-2" /> Laporan Bulanan</Button>
                </CardContent></Card>
                 <Card><CardHeader><CardTitle className="text-base">Statistik Aplikasi</CardTitle></CardHeader><CardContent className="text-sm">
                    <p>Total Murid: {students.length}</p>
                    <p>Total Wali Kelas: {teachers.length}</p>
                    <p>Total Absensi: {attendanceRecords.length}</p>
                </CardContent></Card>
              </div>
              <Card><CardHeader><CardTitle>Daftar Murid</CardTitle><CardContent>
                    <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm dark:bg-slate-800 dark:border-slate-700">
                        <option value="all">Semua Kelas</option>
                        {[...new Set(students.map(s => s.class))].sort().map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
              </CardContent></CardHeader><CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b dark:border-slate-700"><th className="text-left p-2">Nama</th><th className="text-left p-2">NIS</th><th className="text-left p-2">Kelas</th><th className="text-center p-2">Status Hari Ini</th><th className="text-center p-2">Keterangan</th></tr></thead>
                  <tbody>
                    {filteredStudents.map(student => {
                      const today = new Date().toDateString();
                      const todayRecords = attendanceRecords.filter(r => new Date(r.timestamp).toDateString() === today && r.studentDocId === student.id);
                      const hasMasuk = todayRecords.some(r => r.type === 'masuk');
                      const hasPulang = todayRecords.some(r => r.type === 'pulang');
                      const lastPhotoToday = todayRecords.slice().reverse().find(r => r.faceImage)?.faceImage;
                      const absenceInfo = getWeeklyAbsenceInfo(student.id);
                      return (
                        <tr key={student.id} className="border-b dark:border-slate-700 hover:bg-muted/50 dark:hover:bg-slate-800/50">
                          <td className="p-2"><div className="flex items-center gap-2">
                            <Avatar className="w-10 h-10"><AvatarImage src={lastPhotoToday || student.faceData} /><AvatarFallback>{student.name.charAt(0)}</AvatarFallback></Avatar>
                            <span className="font-medium">{student.name}</span>
                          </div></td>
                          <td className="p-2">{student.studentId}</td>
                          <td className="p-2">{student.class}</td>
                          <td className="p-2 text-center"><div className="flex gap-1 justify-center">
                            <span className={`text-xs px-2 py-1 rounded ${hasMasuk ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' : 'bg-gray-100 dark:bg-slate-700'}`}>Masuk</span>
                            <span className={`text-xs px-2 py-1 rounded ${hasPulang ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' : 'bg-gray-100 dark:bg-slate-700'}`}>Pulang</span>
                          </div></td>
                          <td className="p-2 text-center">
                            {absenceInfo.showWarning && (
                              <div className="flex justify-center items-center" title={absenceInfo.message}>
                                  <AlertCircle className="w-5 h-5 text-red-500" />
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent></Card>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const TeacherView = () => {
    if (!currentUser || currentUser.role !== 'teacher' || !currentUser.assignedClass) return null;

    const myClass = currentUser.assignedClass;
    const myStudents = students.filter(s => s.class === myClass);
    
    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 p-4 transition-colors">
            <div className="max-w-7xl mx-auto">
                <Card>
                    <CardHeader className="flex flex-row justify-between items-center">
                        <div>
                            <CardTitle>Dashboard Wali Kelas</CardTitle>
                            <CardDescription>{currentUser.name} - Kelas {myClass}</CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                            <ThemeToggleButton />
                            <Button variant="outline" onClick={handleAuthLogout}><LogOut className="w-4 h-4 mr-2" /> Keluar</Button>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Card><CardHeader><CardTitle>Total Murid</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold">{myStudents.length}</p></CardContent></Card>
                            <Card><CardHeader><CardTitle>Hadir Hari Ini</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold">{attendanceRecords.filter(r => r.studentClass === myClass && new Date(r.timestamp).toDateString() === new Date().toDateString() && r.type==='masuk').length}</p></CardContent></Card>
                             <Card><CardHeader><CardTitle>Export Laporan</CardTitle></CardHeader><CardContent className="flex flex-col gap-2">
                                <Button onClick={() => generateExcelReport('daily', myClass)} variant="outline"><Calendar className="w-4 h-4 mr-2" /> Harian</Button>
                                <Button onClick={() => generateExcelReport('monthly', myClass)} variant="outline"><Calendar className="w-4 h-4 mr-2" /> Bulanan</Button>
                            </CardContent></Card>
                        </div>
                         <Card><CardHeader><CardTitle>Daftar Murid Kelas {myClass}</CardTitle></CardHeader><CardContent className="overflow-x-auto">
                            <table className="w-full text-sm">
                            <thead><tr className="border-b dark:border-slate-700"><th className="text-left p-2">Nama</th><th className="text-left p-2">NIS</th><th className="text-center p-2">Status Hari Ini</th><th className="text-center p-2">Keterangan</th></tr></thead>
                            <tbody>
                                {myStudents.map(student => {
                                const todayRecords = attendanceRecords.filter(r => new Date(r.timestamp).toDateString() === new Date().toDateString() && r.studentDocId === student.id);
                                const hasMasuk = todayRecords.some(r => r.type === 'masuk');
                                const hasPulang = todayRecords.some(r => r.type === 'pulang');
                                const lastPhotoToday = todayRecords.slice().reverse().find(r => r.faceImage)?.faceImage;
                                const absenceInfo = getWeeklyAbsenceInfo(student.id);
                                return (
                                    <tr key={student.id} className="border-b dark:border-slate-700 hover:bg-muted/50 dark:hover:bg-slate-800/50">
                                    <td className="p-2"><div className="flex items-center gap-2">
                                        <Avatar className="w-10 h-10"><AvatarImage src={lastPhotoToday || student.faceData} /><AvatarFallback>{student.name.charAt(0)}</AvatarFallback></Avatar>
                                        <span className="font-medium">{student.name}</span>
                                    </div></td>
                                    <td className="p-2">{student.studentId}</td>
                                    <td className="p-2 text-center"><div className="flex gap-1 justify-center">
                                        <span className={`text-xs px-2 py-1 rounded ${hasMasuk ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' : 'bg-gray-100 dark:bg-slate-700'}`}>Masuk</span>
                                        <span className={`text-xs px-2 py-1 rounded ${hasPulang ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' : 'bg-gray-100 dark:bg-slate-700'}`}>Pulang</span>
                                    </div></td>
                                    <td className="p-2 text-center">
                                        {absenceInfo.showWarning && (
                                        <div className="flex justify-center items-center" title={absenceInfo.message}>
                                            <AlertCircle className="w-5 h-5 text-red-500" />
                                        </div>
                                        )}
                                    </td>
                                    </tr>
                                );
                                })}
                            </tbody>
                            </table>
                        </CardContent></Card>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
  };
  
  const CameraModal = () => {
    if (!isCameraOpen) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-lg dark:bg-slate-900 dark:border-slate-700">
          <CardHeader>
            <CardTitle>Absen {attendanceTypeTemp}</CardTitle>
            <CardDescription>Posisikan wajah Anda dan ambil foto.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative w-full aspect-video bg-gray-200 rounded-lg overflow-hidden">
              {!capturedPhoto ? <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover"></video> : <img src={capturedPhoto} alt="Captured" className="w-full h-full object-cover" />}
              <canvas ref={canvasRef} className="hidden"></canvas>
              {cameraError && <div className="absolute inset-0 flex items-center justify-center text-red-500 p-4">{cameraError}</div>}
            </div>
            <div className="flex gap-4">
              {capturedPhoto ? (
                <>
                  <Button variant="outline" className="w-full" onClick={handleRetake}><RefreshCw className="mr-2 h-4 w-4"/>Ulangi</Button>
                  <Button className="w-full" onClick={handleConfirmAttendance}><Check className="mr-2 h-4 w-4"/>Konfirmasi Absen</Button>
                </>
              ) : (<Button className="w-full" onClick={capturePhoto} disabled={!!cameraError}><Camera className="mr-2 h-4 w-4"/>Ambil Foto</Button>)}
            </div>
            <Button variant="ghost" className="w-full" onClick={closeCamera}>Batal</Button>
          </CardContent>
        </Card>
      </div>
    );
  };
  
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-slate-900">Memuat...</div>;
  }
  
  return (
    <>
      {currentView === 'login' && <LoginView />}
      {currentView === 'studentLogin' && <StudentLoginView />}
      {currentView === 'adminLogin' && <AuthLoginView role="admin" />}
      {currentView === 'teacherLogin' && <AuthLoginView role="teacher" />}
      {currentView === 'student' && <StudentView />}
      {currentView === 'admin' && currentUser?.role === 'admin' && <AdminView />}
      {currentView === 'teacher' && currentUser?.role === 'teacher' && <TeacherView />}
      <CameraModal />
    </>
  );
};

export default OnlineAttendanceSystem;

